import { roundRect2 } from '../../renderers';
import type { BasicObject } from '../../uiObjects/basicObject';
import { getBasicButton } from '../../uiObjects/button';

interface LockedGridModeSelectorVisualStateOptions {
  selected: boolean;
  correct: boolean;
  moveModeActive: boolean;
}

interface LockedGridModeSelectorVisualState {
  backgroundColor?: string;
  textColor: string;
}

interface PaintGridModeLockIconOptions {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  color: string;
}

interface ShouldShowGridModeSelectorRowOptions {
  fixed: boolean;
  hasDragButton: boolean;
  actingAsNumberLineGetter: boolean;
}

interface LockedGridModeDialog {
  top: string;
  msg: string;
  removeWhenAnotherDialogShows: boolean;
}

interface LockedGridModeSelectorGetter {
  g?: {
    selected?: boolean;
  };
  st?: string;
  isGridMoveModeActive?: () => boolean;
}

type GridModeSelectorLabel = BasicObject & {
  stamp: BasicObject;
};

interface GetLockedGridModeSelectorOptions {
  $canvas: JQuery<HTMLElement>;
  mode: string;
  label: GridModeSelectorLabel;
  getter: LockedGridModeSelectorGetter;
  showDialog: (dialog: LockedGridModeDialog) => void;
}

const ACTIVE_BACKGROUND = '#2f80ed';
const ACTIVE_TEXT = '#ffffff';
const ENABLED_BACKGROUND = 'lightgray';
const ENABLED_TEXT = 'gray';
const SELECTED_CORRECT_TEXT = '#a8a8a8';
const UNSELECTED_TEXT = 'lightgray';
const BASE_ICON_SIZE = 30;

export const getLockedGridModeSelectorVisualState = ({
  selected,
  correct,
  moveModeActive,
}: LockedGridModeSelectorVisualStateOptions): LockedGridModeSelectorVisualState => {
  const available = selected && !correct;
  const active = available && !moveModeActive;

  if (!selected || correct) {
    return {
      textColor: selected ? SELECTED_CORRECT_TEXT : UNSELECTED_TEXT,
    };
  }

  return {
    backgroundColor: active ? ACTIVE_BACKGROUND : ENABLED_BACKGROUND,
    textColor: active ? ACTIVE_TEXT : ENABLED_TEXT,
  };
};

export const shouldShowGridModeSelectorRow = ({
  fixed,
  hasDragButton,
  actingAsNumberLineGetter,
}: ShouldShowGridModeSelectorRowOptions): boolean =>
  !fixed || hasDragButton || !actingAsNumberLineGetter;

export const getLockedGridModeDialog = (
  mode: string
): LockedGridModeDialog => ({
  top: 'Graphing Tool Locked',
  msg: `This problem uses the ${mode} tool. Some other graphing problems let you choose a different tool, but the tool is fixed for this problem.`,
  removeWhenAnotherDialogShows: true,
});

export const getLockedGridModeSelector = ({
  $canvas,
  mode,
  label,
  getter,
  showDialog,
}: GetLockedGridModeSelectorOptions): BasicObject => {
  const isSelected = (): boolean => Boolean(getter.g?.selected);
  const isCorrect = (): boolean => getter.st === 'c';

  const getVisualState = (): LockedGridModeSelectorVisualState =>
    getLockedGridModeSelectorVisualState({
      selected: isSelected(),
      correct: isCorrect(),
      moveModeActive: Boolean(getter.isGridMoveModeActive?.()),
    });

  label.paintPre = function () {
    label.stamp.fillStyle = getVisualState().textColor;
  };

  const lock = getBasicButton($canvas);

  lock.setAllDim(BASE_ICON_SIZE, BASE_ICON_SIZE);

  lock.paintMe = function (ctx) {
    if (!isSelected() || isCorrect()) {
      return;
    }

    paintGridModeLockIcon({
      ctx,
      width: lock.viewportW,
      height: lock.viewportH,
      color: getVisualState().textColor,
    });
  };

  const selector = getBasicButton($canvas);

  selector.add(lock);
  selector.add(label);
  selector.layoutChildrenLR(8);
  selector.sizeMeToFitChildren();
  selector.animates = false;

  selector.paintPre = function (ctx) {
    const backgroundColor = getVisualState().backgroundColor;

    if (!backgroundColor) {
      return;
    }

    roundRect2(
      ctx,
      0,
      0,
      selector.viewportW,
      selector.viewportH,
      6,
      backgroundColor,
      undefined,
      undefined,
      true
    );
  };

  selector.mouseDownResponse = function () {
    if (!isCorrect()) {
      showDialog(getLockedGridModeDialog(mode));
    }

    return true;
  };

  selector.mouseMoveResponse = function () {
    $canvas.css('cursor', 'default');
  };

  return selector;
};

export const paintGridModeLockIcon = ({
  ctx,
  width,
  height,
  color,
}: PaintGridModeLockIconOptions): void => {
  const scale = Math.min(width, height) / BASE_ICON_SIZE;
  const centerX = width / 2;
  const shackleCenterY = 12 * scale;
  const shackleRadius = 5 * scale;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2 * scale;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(centerX, shackleCenterY, shackleRadius, Math.PI, 0);
  ctx.stroke();

  roundRect2(
    ctx,
    centerX - 7 * scale,
    shackleCenterY,
    14 * scale,
    11 * scale,
    2 * scale,
    color,
    undefined,
    undefined,
    true
  );
  ctx.restore();
};
